import { Data, DeepStructure, Language, Structure } from '@wix/thunderbolt-becky-types'
import { flattenStructure, getChildrenKey } from '../../flattenStructure'
import _ from 'lodash'
import type { BeckyRequestPayload } from '@wix/thunderbolt-becky-root'

export type ProcessedPages = {
	data: Data
	masterPageTranslations: Record<Language, { data: Pick<Data, 'document_data'> }>
	translations: Record<Language, { data: Pick<Data, 'document_data'> }>
	pageCompId: string
	masterPageStructure: Structure
	structure: Structure
}

export const getRequestedPageCompId = (pages: BeckyRequestPayload['pages']): string =>
	_.findKey(pages, (v, k) => k !== 'masterPage') || 'masterPage'

export const mergePagesData = (pageData: Data, masterPageData: Data, isResponsive: boolean) => {
	if (!isResponsive) {
		return _.merge({}, masterPageData, pageData)
	} else {
		const minimalMasterPageData = _.mapValues(masterPageData, (dataMap, mapName) => {
			if (mapName === 'document_data') {
				return _.omitBy(dataMap, (dataItem) => dataItem.type === 'Anchor')
			}
			if (mapName === 'component_properties' || mapName === 'connections_data' || mapName === 'behaviors_data') {
				return {}
			}

			return dataMap
		})

		return _.merge({}, minimalMasterPageData, pageData)
	}
}

const mergeTranslations = (
	pageTranslations: ProcessedPages['translations'],
	masterPageTranslations: ProcessedPages['translations']
): ProcessedPages['translations'] => {
	return _.merge({}, masterPageTranslations, pageTranslations)
}

const getResponsiveCleanMasterPageStructure = (structure: DeepStructure, isMobile: boolean) => {
	const childrenKey = getChildrenKey(structure, isMobile)
	const children = _.filter(structure[childrenKey], (val) =>
		['masterPage', 'SITE_PAGES', 'SITE_HEADER', 'SITE_FOOTER', 'PAGES_CONTAINER'].includes(val.id)
	)
	return flattenStructure({ ...structure, [childrenKey]: children }, isMobile)
}
export const getMergedDataWithFlatPageStructure = (
	pages: BeckyRequestPayload['pages'],
	viewMode: BeckyRequestPayload['viewMode'],
	isResponsive: boolean
): Promise<ProcessedPages> => {
	const pageCompId = getRequestedPageCompId(pages)

	return Promise.all([pages.masterPage, pages[pageCompId]]).then(([masterPage, page]) => {
		const isMasterPage = pageCompId === 'masterPage'
		const isMobile = viewMode === 'mobile'
		const data = isMasterPage ? masterPage.data : mergePagesData(page.data, masterPage.data, isResponsive)
		const structure =
			isMasterPage && isResponsive
				? getResponsiveCleanMasterPageStructure(page.structure, isMobile)
				: flattenStructure(page.structure, isMobile)
		const masterPageStructure = flattenStructure(masterPage.structure, isMobile)
		const translations = isMasterPage
			? masterPage.translations
			: mergeTranslations(page.translations, masterPage.translations)
		const masterPageTranslations = masterPage.translations

		return {
			masterPageStructure,
			structure,
			data,
			pageCompId,
			translations,
			masterPageTranslations,
		}
	})
}
